import React from 'react';
import styles from './SectionTable.module.css';
import SectionTable from './SectionTable';
import Ico from '../common/Ico';
import Pill from '../common/Pill';
import { statusLabel } from './statusConfig';
import ActorCell from './ActorCell';
import RolloutCell from './RolloutCell';
import { timeSinceTS } from '../../helpers/dates';
import { nightlyColumns, visibleAutLanes } from './nightlyAutLanes';

const ICO_MOON      = 'M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36A5.39 5.39 0 0 1 12.5 3.1 9.06 9.06 0 0 0 12 3z';
const ICO_PLAY      = 'm5 3 14 9-14 9V3z';
const ICO_CHEVRIGHT = 'm9 18 6-6-6-6';
const ICO_ROCKET    = 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z';

const ICO_CHECK  = 'M20 6 9 17l-5-5';
const ICO_XMARK  = 'M18 6 6 18M6 6l12 12';
const ICO_EXPAND = 'M16 3h5v5M21 3l-7 7M8 21H3v-5M3 21l7-7';
const ICO_SKIP   = 'M5 4l10 8-10 8V4zM19 5v14';

function AutStat({ outcome }) {
  if (!outcome || outcome.expected == null) return <span className={styles.autEmpty}>—</span>;
  return (
    <span className={styles.autStats}>
      <span className={styles.statOk}>
        <Ico d={ICO_CHECK} size={11} strokeWidth={3} />
        {outcome.expected.toLocaleString()}
      </span>
      {outcome.unexpected > 0 && (
        <span className={styles.statErr}>
          <Ico d={ICO_XMARK} size={10} strokeWidth={3} />
          {outcome.unexpected}
        </span>
      )}
      {outcome.flaky > 0 && (
        <span className={styles.statFlaky}>
          <Ico d={ICO_EXPAND} size={10} strokeWidth={2.4} />
          {outcome.flaky}
        </span>
      )}
      {outcome.skipped > 0 && (
        <span className={styles.statSkipped}>
          <Ico d={ICO_SKIP} size={10} strokeWidth={2} />
          {outcome.skipped}
        </span>
      )}
    </span>
  );
}

function renderRow(run, lanes) {
  const buildTag = run.build_tag || run.result?.build_docker?.image_tag;
  return (
    <>
      <td className={styles.td}>
        <span className={styles.numCell}>
          <span className={styles.mono}>
            #{run.number}
            {run.rollout_created && (
              <Ico d={ICO_ROCKET} size={12} strokeWidth={2} className={styles.deployBadge} />
            )}
          </span>
          {buildTag && (
            <code className={styles.imageTag}>{buildTag}</code>
          )}
        </span>
      </td>
      <td className={styles.td}>
        <Pill status={run.status} dot pulse={run.status === 'running'}>
          {statusLabel(run.status)}
        </Pill>
      </td>
      {lanes.map(({ key }) => (
        <td className={styles.td} key={key}>
          <AutStat outcome={run.result?.[key]?.outcome} />
        </td>
      ))}
      <td className={styles.td}>
        <RolloutCell deployStatus={run.deployments} />
      </td>
      <td className={styles.td}>
        <ActorCell actor={run.triggered_by} />
      </td>
      <td className={`${styles.td} ${styles.tdRight}`}>
        <span className={styles.when}>
          <span>{timeSinceTS(run.triggered_at)}</span>
          <Ico d={ICO_CHEVRIGHT} size={16} style={{ color: 'var(--gray-400)' }} />
        </span>
      </td>
    </>
  );
}

export default function NightlySection({ runs = [], page, totalCount, onPageChange, onSearchChange, onTrigger, onOpenRun }) {
  // Headers and cells are both derived from the same lane list so they cannot drift apart.
  const lanes = visibleAutLanes(runs);
  return (
    <SectionTable
      id="sec-nightly"
      icon={ICO_MOON}
      title="Nightly runs"
      subtitle="Builds the branch nightly, runs upgrade tests (AUTs) & E2E, then deploys to the test instance."
      searchPlaceholder="Search run #…"
      triggerLabel="Trigger nightly"
      triggerIcon={ICO_PLAY}
      onTrigger={onTrigger}
      items={runs}
      columns={nightlyColumns(runs)}
      renderRow={(run) => renderRow(run, lanes)}
      onOpenItem={onOpenRun}
      pageSize={10}
      emptyTitle="No nightly runs yet"
      emptyHint="Trigger one to get started."
      pagination="server"
      page={page}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onSearchChange={onSearchChange}
    />
  );
}
